/**
 * SkeletonProfile - defines bone naming conventions for animation retargeting.
 * Maps bone names to semantic roles (e.g., "mixamorigHips" -> Hips) to enable
 * animation retargeting between different skeleton conventions.
 */

/** Semantic bone roles for humanoid characters. */
export enum BoneRole {
  // Core
  Root = 'Root',
  Hips = 'Hips',
  Spine = 'Spine',
  Chest = 'Chest',
  UpperChest = 'UpperChest',
  Neck = 'Neck',
  Head = 'Head',

  // Left Arm
  LeftShoulder = 'LeftShoulder',
  LeftUpperArm = 'LeftUpperArm',
  LeftLowerArm = 'LeftLowerArm',
  LeftHand = 'LeftHand',

  // Right Arm
  RightShoulder = 'RightShoulder',
  RightUpperArm = 'RightUpperArm',
  RightLowerArm = 'RightLowerArm',
  RightHand = 'RightHand',

  // Left Leg
  LeftUpperLeg = 'LeftUpperLeg',
  LeftLowerLeg = 'LeftLowerLeg',
  LeftFoot = 'LeftFoot',
  LeftToeBase = 'LeftToeBase',

  // Right Leg
  RightUpperLeg = 'RightUpperLeg',
  RightLowerLeg = 'RightLowerLeg',
  RightFoot = 'RightFoot',
  RightToeBase = 'RightToeBase',

  // Fingers (Left Hand)
  LeftHandThumb1 = 'LeftHandThumb1',
  LeftHandThumb2 = 'LeftHandThumb2',
  LeftHandThumb3 = 'LeftHandThumb3',
  LeftHandIndex1 = 'LeftHandIndex1',
  LeftHandIndex2 = 'LeftHandIndex2',
  LeftHandIndex3 = 'LeftHandIndex3',
  LeftHandMiddle1 = 'LeftHandMiddle1',
  LeftHandMiddle2 = 'LeftHandMiddle2',
  LeftHandMiddle3 = 'LeftHandMiddle3',
  LeftHandRing1 = 'LeftHandRing1',
  LeftHandRing2 = 'LeftHandRing2',
  LeftHandRing3 = 'LeftHandRing3',
  LeftHandPinky1 = 'LeftHandPinky1',
  LeftHandPinky2 = 'LeftHandPinky2',
  LeftHandPinky3 = 'LeftHandPinky3',

  // Fingers (Right Hand)
  RightHandThumb1 = 'RightHandThumb1',
  RightHandThumb2 = 'RightHandThumb2',
  RightHandThumb3 = 'RightHandThumb3',
  RightHandIndex1 = 'RightHandIndex1',
  RightHandIndex2 = 'RightHandIndex2',
  RightHandIndex3 = 'RightHandIndex3',
  RightHandMiddle1 = 'RightHandMiddle1',
  RightHandMiddle2 = 'RightHandMiddle2',
  RightHandMiddle3 = 'RightHandMiddle3',
  RightHandRing1 = 'RightHandRing1',
  RightHandRing2 = 'RightHandRing2',
  RightHandRing3 = 'RightHandRing3',
  RightHandPinky1 = 'RightHandPinky1',
  RightHandPinky2 = 'RightHandPinky2',
  RightHandPinky3 = 'RightHandPinky3',

  // Head (Optional)
  RightEye = 'RightEye',
  LeftEye = 'LeftEye',
  Jaw = 'Jaw',
}

/**
 * Axis flip configuration for position retargeting.
 * Used to handle coordinate system differences between skeletons.
 */
export interface PositionAxisFlips {
  /** Flip X axis for root motion */
  x?: boolean;
  /** Flip Y axis for root motion */
  y?: boolean;
  /** Flip Z axis for root motion */
  z?: boolean;
}

/**
 * Skeleton profile - maps bone names to semantic roles.
 * Can be serialized to/from JSON for asset storage.
 */
export interface SkeletonProfile {
  /** Profile name (e.g., "Mixamo", "UE5", "Standard") */
  name: string;

  /** Human-readable description */
  description: string;

  /** Map bone name -> semantic role */
  boneToRole: Record<string, BoneRole>;

  /** Hip bone name (required for retargeting root motion) */
  hipBoneName: string;

  /** Optional scale factor for unit conversion (e.g., 0.01 for cm to m) */
  scale?: number;

  /**
   * Optional axis flips for root position retargeting.
   * Used when this profile is the TARGET to correct coordinate system differences.
   */
  positionAxisFlips?: PositionAxisFlips;

  /**
   * Optional axis flips for hips position retargeting.
   * Separate from root because hips may have different coordinate system.
   */
  hipsPositionAxisFlips?: PositionAxisFlips;

  /**
   * Force T-pose application when this profile is used in retargeting.
   * When true, the skeleton will be forced into a T-pose before retargeting
   * calculations, regardless of its current/bind pose.
   */
  forceTPose?: boolean;
}

/**
 * Build a role -> bone name map from a skeleton profile.
 * Used to resolve BoneRole values to concrete bone names for masking.
 */
export function buildRoleToBoneMap(profile: SkeletonProfile): Map<BoneRole, string> {
  const roleToBone = new Map<BoneRole, string>();
  for (const [boneName, role] of Object.entries(profile.boneToRole)) {
    roleToBone.set(role, boneName);
  }
  return roleToBone;
}

/**
 * Resolve a list of bone roles to concrete bone names using a skeleton profile.
 * Logs warnings for any roles not found in the profile.
 * Returns the resolved bone names and a set of unresolved roles.
 */
export function resolveRolesToBones(
  roles: string[],
  profile: SkeletonProfile,
  contextLabel?: string
): { resolved: string[]; unresolved: Set<string> } {
  const roleToBone = buildRoleToBoneMap(profile);
  const resolved: string[] = [];
  const unresolved = new Set<string>();

  for (const role of roles) {
    const boneName = roleToBone.get(role as BoneRole);
    if (boneName) {
      resolved.push(boneName);
    } else {
      unresolved.add(role);
    }
  }

  if (unresolved.size > 0 && contextLabel) {
    console.warn(
      `${contextLabel}: Could not resolve roles to bones: ${Array.from(unresolved).join(', ')}`
    );
  }

  return { resolved, unresolved };
}

